'use strict';

import React, {useState, useMemo, useCallback} from 'react';
import {apiCall} from "../../shared/api-client";

import {getFieldName, getFieldTitle, formatMinutes, formatNumber, formatSessionLength} from "./shared";
import {MetricsTableTR, MetricsTableColumns, getIntervalValue} from "./metrics-table";
import {useDashboardFilter, useStaleDataLoader, isStale} from "./state";
import config from "../../shared/config";
import {pretty, cssClasses} from "../../shared/utils";

const APIMethod = 'dashboard/metrics-by-filter';

const SortDesc = 'desc';
const SortAsc = 'asc';
const ShowRows = 9;

function MetricsByFilterTable({data, getter, formatter, className}) {
	const [sort, setSort] = useState(['day7', SortDesc]);
	const [showExtra, setExtra] = useState(false);
	const prepareRows = useCallback((data) => {
		let [column, order] = sort;
		let rows = Array.from(Object.entries(data))
			.filter(
				// API's giving back the total with an empty market.
				r => Boolean(r[0])
			)
			.map(
				r => {
					let [title, data] = r;
					return {
						value: getIntervalValue(data, column, getter),
						title, data
					};
				}
			);

		let sorter = (r1, r2) => {
			let v1 = r1.value;
			let v2 = r2.value;
			if (v1 === v2) {
				return 0;
			}
			if (!v1) {
				return 1;
			}
			if (!v2) {
				return -1;
			}
			let s = Math.sign(v1 - v2);
			if (order === SortAsc) {
				return s;
			} else {
				return -s;
			}
		};
		rows.sort(sorter);
		let hidden = 0;
		if (!showExtra) {
			hidden = rows.splice(ShowRows).length;
		}
		return [rows, hidden];
	}, [getter, sort, showExtra]);

	const onSortChange = useCallback((key) => {
		setSort(([s, o]) => {
			let no;
			if (s === key) {
				no = o === SortDesc ? SortAsc : SortDesc;
			} else {
				no = SortDesc;
			}
			return [key, no];
		});
	}, [setSort]);

	let [rows, hidden] = prepareRows(data);
	let span = MetricsTableColumns.length + 2;

	return <table className={cssClasses("metrics-table cm-table table table-stripped", className)}>
		<thead>
			<tr>
				<th key="title">Metric</th>
				<th key="sparks"/>
				{
					MetricsTableColumns.map(
						([t, k]) => (
							<th key={k} className={cssClasses('sort', k === sort[0] && sort[1])} data-key={k}>
								<span onClick={
									e => {
										e.preventDefault();
										onSortChange(k);
									}
								}>{t}</span>
							</th>
						)
					)
				}
			</tr>
		</thead>
		<tbody>
			{
				rows.map(
					({title, data}, i) =>
						<MetricsTableTR
							key={`mt-row-${i}`} data={data}
							row={[title, getter, formatter, data]}/>
				)
			}
			{
				hidden > 0 && (
					<tr>
						<td colSpan={span} className="more">
							<a href="#" onClick={(e) => {
								e.preventDefault();
								setExtra(true);
							}}>
								Show more
							</a>
						</td>
					</tr>
				)
			}
		</tbody>
	</table>;
}

function groupField(group) {
	return Array.isArray(group) ? group[0] : group;
}

function groupTitle(group) {
	return (Array.isArray(group) && group[1]) || pretty(groupField(group));
}

function MetricsBlock({data}) {
	if (!data) {
		return 'Loading data';
	}

	return <>
		<h3>Minutes</h3>
		<MetricsByFilterTable
			data={data} getter="total" formatter={formatMinutes} className="minutes-metrics" />
		<h3>Active streams</h3>
		<MetricsByFilterTable
			data={data} getter="count" formatter={formatNumber} className="streams-metrics" />
		<h3>Average Session Length (minutes)</h3>
		<MetricsByFilterTable
			data={data} getter="mean" formatter={formatSessionLength} className="sessions-metrics" />
		<h3>Peak concurrency</h3>
		<MetricsByFilterTable
			data={data} getter="ccu" formatter={formatNumber} className="ccu-metrics" />
	</>
}

function GroupByToggle({current, groups, set}) {
	let i = 1;
	let re = [];
	for (let g of groups) {
		let fieldName = getFieldName(g);
		if (fieldName === current) {
			re[0] = <span key={fieldName} className="active">Metrics by {getFieldTitle(g)}</span>
		} else {
			re[i++] = <small key={fieldName}><a
				href="#" onClick={e => {
					e.preventDefault();
					set(fieldName);
				}}
			>{getFieldTitle(g)}</a></small>
		}
	}
	return <h2 className="group-by">{re}</h2>;

}

function MetricsByFilter() {
	const metricGroups = config.get('GroupDashboardMetricsBy', null);
	const [data, setData] = useState(null);
	const [filter] = useDashboardFilter();
	const [groupBy, setGroup] = useState(() => {
		if (!Array.isArray(metricGroups)) {
			return null;
		}
		return getFieldName((metricGroups[0]));
	});
	const loader = useMemo(() => {
		return async (cancelled) => {
			if (!groupBy) {
				return false;
			}
			let response = await apiCall({
				method: APIMethod,
				params: Object.assign(filter.toJS(), {groupBy})
			});
			let data = response.data;
			if (!cancelled.current) {
				setData(data);
			}
			return isStale(data);
		};
	}, [setData, filter, groupBy]);
	useStaleDataLoader(loader);

	if (!groupBy) {
		return null;
	}

	return (
		<section id="dashboard-metrics-by-filter">
			<GroupByToggle
				groups={metricGroups} current={groupBy}
				set={(g) => {
					setGroup(g);
					setData(null);
				}}
			/>
			<MetricsBlock data={data} />
		</section>
	);
}

export default MetricsByFilter;